/**
 * CampaignDetailPage — tabbed campaign workspace: Overview (budget +
 * funnel goals + lifecycle/kill switch), Placements (channel deploys),
 * and Creative Studio (AI generation + review + ad deploys). Live
 * metrics land with the dashboard phase.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useAdoptCampaign,
  useAdoptCampaignLifecycle,
  useAdoptKillSwitch,
  usePutAdoptFunnelGoals,
} from '../hooks/useAdvertisingOptimization';
import { PlacementsSection } from '../components/PlacementsSection';
import { CreativeStudioSection } from '../components/CreativeStudioSection';
import type { AdoptFunnelGoalDto, AdoptFunnelGoalInput } from '@/lib/api';

interface Props {
  holderId: string;
}

const GOAL_TYPES = [
  'click',
  'landing_view',
  'signup',
  'activation',
  'purchase',
  'custom_event',
] as const;

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function FunnelGoalsEditor({
  holderId,
  campaignId,
  goals,
}: {
  holderId: string;
  campaignId: string;
  goals: AdoptFunnelGoalDto[];
}) {
  const putGoals = usePutAdoptFunnelGoals(holderId, campaignId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AdoptFunnelGoalInput[]>([]);

  const startEdit = (): void => {
    setDraft(
      goals.length > 0
        ? goals.map((g) => ({
            stageOrder: g.stageOrder,
            name: g.name,
            goalType: g.goalType,
            eventKey: g.eventKey,
            isPrimary: g.isPrimary,
          }))
        : [
            { stageOrder: 0, name: 'Ad click', goalType: 'click', eventKey: 'ad_click' },
            {
              stageOrder: 1,
              name: 'Signup',
              goalType: 'signup',
              eventKey: 'signup',
              isPrimary: true,
            },
          ],
    );
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    await putGoals.mutateAsync(draft);
    setEditing(false);
  };

  if (!editing) {
    return (
      <section className="rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-medium">Funnel goals</h2>
          <button onClick={startEdit} className="text-sm text-primary hover:underline">
            {goals.length > 0 ? 'Edit' : 'Define goals'}
          </button>
        </div>
        {goals.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No funnel goals yet — required before activation.
          </p>
        ) : (
          <ol className="flex flex-col gap-1">
            {goals.map((g) => (
              <li key={g.id} className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground w-5">{g.stageOrder + 1}.</span>
                <span className="font-medium">{g.name}</span>
                <span className="text-xs text-muted-foreground">({g.goalType})</span>
                {g.isPrimary && (
                  <span className="text-xs rounded bg-primary/10 text-primary px-1.5 py-0.5">
                    primary
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <h2 className="font-medium">Edit funnel goals</h2>
      {draft.map((g, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground w-5">{i + 1}.</span>
          <input
            className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={g.name}
            onChange={(e) =>
              setDraft(draft.map((d, j) => (j === i ? { ...d, name: e.target.value } : d)))
            }
            placeholder="Stage name"
          />
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={g.goalType}
            onChange={(e) =>
              setDraft(
                draft.map((d, j) =>
                  j === i
                    ? { ...d, goalType: e.target.value as AdoptFunnelGoalInput['goalType'] }
                    : d,
                ),
              )
            }
          >
            {GOAL_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <input
            className="w-36 rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={g.eventKey}
            onChange={(e) =>
              setDraft(draft.map((d, j) => (j === i ? { ...d, eventKey: e.target.value } : d)))
            }
            placeholder="event_key"
          />
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="radio"
              name="primary-goal"
              checked={!!g.isPrimary}
              onChange={() => setDraft(draft.map((d, j) => ({ ...d, isPrimary: j === i })))}
            />
            primary
          </label>
          <button
            onClick={() =>
              setDraft(draft.filter((_, j) => j !== i).map((d, j) => ({ ...d, stageOrder: j })))
            }
            className="text-sm text-rose-600"
            aria-label={`Remove stage ${i + 1}`}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <button
          onClick={() =>
            setDraft([
              ...draft,
              { stageOrder: draft.length, name: '', goalType: 'custom_event', eventKey: '' },
            ])
          }
          className="text-sm text-primary hover:underline"
        >
          + Add stage
        </button>
      </div>
      {putGoals.isError && (
        <div className="text-sm text-rose-600">
          Failed to save goals — check every stage has a name and event key.
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => void save()}
          disabled={putGoals.isPending || draft.some((d) => !d.name.trim() || !d.eventKey.trim())}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          Save
        </button>
        <button
          onClick={() => setEditing(false)}
          className="px-3 py-1.5 rounded-md border border-border text-sm"
        >
          Cancel
        </button>
      </div>
    </section>
  );
}

const DETAIL_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'placements', label: 'Placements' },
  { key: 'creatives', label: 'Creative Studio' },
] as const;

type DetailTab = (typeof DETAIL_TABS)[number]['key'];

export function CampaignDetailPage({ holderId }: Props) {
  const { campaignId } = useParams<{ campaignId: string }>();
  const detailQuery = useAdoptCampaign(holderId, campaignId);
  const lifecycle = useAdoptCampaignLifecycle(holderId, campaignId ?? '');
  const killSwitch = useAdoptKillSwitch(holderId, campaignId ?? '');
  const [tab, setTab] = useState<DetailTab>('overview');

  if (detailQuery.isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (!detailQuery.data)
    return <div className="p-6 text-muted-foreground">Campaign not found.</div>;

  const { campaign, funnelGoals } = detailQuery.data;
  const canActivate = campaign.status === 'draft' || campaign.status === 'paused';
  const canPause = campaign.status === 'active';

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{campaign.name}</h1>
          <p className="text-sm text-muted-foreground capitalize">
            {campaign.status} · {campaign.objective.toUpperCase()} ·{' '}
            {campaign.promotedArcheId ??
              (campaign.promotedTarget as { label?: string }).label ??
              ''}
          </p>
        </div>
        <div className="flex gap-2">
          {canActivate && (
            <button
              onClick={() => lifecycle.mutate('activate')}
              disabled={lifecycle.isPending || campaign.killSwitch}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              Activate
            </button>
          )}
          {canPause && (
            <button
              onClick={() => lifecycle.mutate('pause')}
              disabled={lifecycle.isPending}
              className="px-3 py-1.5 rounded-md border border-border text-sm"
            >
              Pause
            </button>
          )}
          <button
            onClick={() => killSwitch.mutate(!campaign.killSwitch)}
            disabled={killSwitch.isPending}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              campaign.killSwitch
                ? 'border-border'
                : 'border-rose-300 text-rose-700 hover:bg-rose-50'
            }`}
          >
            {campaign.killSwitch ? 'Release kill switch' : 'Kill switch'}
          </button>
        </div>
      </header>

      {lifecycle.isError && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {lifecycle.error instanceof Error ? lifecycle.error.message : 'Action failed'}
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-border">
        {DETAIL_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg border border-border p-4">
              <div className="text-sm text-muted-foreground">Total budget</div>
              <div className="text-xl font-semibold">{centsToUsd(campaign.totalBudgetCents)}</div>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="text-sm text-muted-foreground">Daily ceiling</div>
              <div className="text-xl font-semibold">
                {centsToUsd(campaign.dailyBudgetCeilingCents)}
              </div>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="text-sm text-muted-foreground">Autonomy</div>
              <div className="text-xl font-semibold capitalize">{campaign.autonomyLevel}</div>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="text-sm text-muted-foreground">Objective target</div>
              <div className="text-xl font-semibold">
                {campaign.objectiveTargetValue !== null ? campaign.objectiveTargetValue : '—'}
              </div>
            </div>
          </div>

          <FunnelGoalsEditor holderId={holderId} campaignId={campaign.id} goals={funnelGoals} />
        </>
      )}

      {tab === 'placements' && (
        <PlacementsSection
          holderId={holderId}
          campaignId={campaign.id}
          dailyBudgetCeilingCents={campaign.dailyBudgetCeilingCents}
        />
      )}

      {tab === 'creatives' && (
        <CreativeStudioSection holderId={holderId} campaignId={campaign.id} />
      )}
    </div>
  );
}
